import * as React from "react";
import { cn } from "@/lib/utils";

/** Responsive page container: max-width + token-driven gutters. */
export const Container = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("ds-container", className)} {...props} />
);

type SectionProps = React.HTMLAttributes<HTMLElement> & {
  tone?: "default" | "mist" | "dark";
  bleed?: boolean;
};

/** Page band with disciplined vertical rhythm and optional tone. */
export const Section = ({ className, tone = "default", bleed = false, children, ...props }: SectionProps) => (
  <section
    className={cn(
      "ds-section",
      tone === "mist" && "band-alt",
      tone === "dark" && "band-dark",
      className,
    )}
    {...props}
  >
    {bleed ? children : <Container>{children}</Container>}
  </section>
);

/** Twelve-column desktop grid (4 cols mobile / 8 tablet / 12 desktop). */
export const Grid12 = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("ds-grid-12", className)} {...props} />
);

const spanMap: Record<number, string> = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
  5: "lg:col-span-5",
  6: "lg:col-span-6",
  7: "lg:col-span-7",
  8: "lg:col-span-8",
  9: "lg:col-span-9",
  10: "lg:col-span-10",
  11: "lg:col-span-11",
  12: "lg:col-span-12",
};

const mdSpanMap: Record<number, string> = {
  1: "md:col-span-1",
  2: "md:col-span-2",
  3: "md:col-span-3",
  4: "md:col-span-4",
  5: "md:col-span-5",
  6: "md:col-span-6",
  7: "md:col-span-7",
  8: "md:col-span-8",
};

const smSpanMap: Record<number, string> = {
  1: "col-span-1",
  2: "col-span-2",
  3: "col-span-3",
  4: "col-span-4",
};

export const Col = ({
  span = 12,
  md,
  sm = 4,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { span?: number; md?: number; sm?: number }) => (
  <div
    className={cn(
      smSpanMap[Math.min(sm, 4)],
      mdSpanMap[Math.min(md ?? Math.min(span, 8), 8)],
      spanMap[Math.min(span, 12)],
      className,
    )}
    {...props}
  />
);

/** Eyebrow + heading + optional description block used above section content. */
export const SectionHeader = ({
  eyebrow,
  title,
  description,
  align = "left",
  className,
}: {
  eyebrow?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  align?: "left" | "center";
  className?: string;
}) => (
  <header className={cn("max-w-prose", align === "center" && "mx-auto text-center", className)}>
    {eyebrow ? <p className="eyebrow eyebrow-dot mb-3">{eyebrow}</p> : null}
    <h2 className="ds-h2">{title}</h2>
    {description ? <p className="ds-lead mt-4">{description}</p> : null}
  </header>
);
